import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Sparkles, LifeBuoy, Check, Pencil, Truck } from "lucide-react";
import DeliverySearchBar from "./DeliverySearchBar";
import { loadDeliveryInfo } from "../lib/checkoutStorage";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FallbackProductFinder = ({ product }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [saved] = useState(() => loadDeliveryInfo());
  const [editing, setEditing] = useState(!saved);

  const onFound = async (res) => {
    if (res.is_fallback || res.shop_id === product.shop_id) {
      setResult({ type: "fallback", shop_name: res.shop_name });
      setEditing(false);
      toast.info("Nessun fiorista affiliato nella tua zona: ci pensa Melaverde con consegna in tutta Italia.");
      return;
    }
    try {
      const m = await axios.get(`${API}/products/${product.id}/match`, { params: { shop_id: res.shop_id } });
      if (m.data.product) {
        toast.success(m.data.kind === "same" ? `Stesso articolo disponibile da ${m.data.shop_name}, il fiorista della tua zona` : `Da ${m.data.shop_name} ti proponiamo l'articolo più simile`);
        navigate(`/products/${m.data.product.id}`);
      } else {
        toast.success(`${m.data.shop_name} è il fiorista della tua zona: scopri la sua vetrina`);
        navigate(`/shops/${res.shop_id}`);
      }
    } catch {
      navigate(`/shops/${res.shop_id}`);
    }
  };

  const known = saved && !editing;
  return (
    <div className="mt-6 rounded-xl border border-brand/20 bg-brand/5 p-4" data-testid="fallback-product-finder">
      {known ? (
        <div data-testid="fallback-finder-known">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-forest flex items-center gap-2"><MapPin className="h-4 w-4 text-brand" />Consegna a {saved.address ? `${saved.address}, ` : ""}{saved.city}</p>
              <p className="text-xs text-sage mt-1">Nella tua zona non c'è ancora un fiorista affiliato: l'ordine è preparato e consegnato tramite <strong>Melaverde</strong>, servizio di copertura Flowerlive. Puoi aggiungere al carrello e procedere.</p>
              {saved.delivery_cost != null && <p className="text-xs text-forest mt-2 flex items-start gap-1.5" data-testid="fallback-finder-delivery"><Truck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-brand" />Consegna € {Number(saved.delivery_cost).toFixed(2)}: include il servizio di copertura per le città non servite (ricerca e selezione del fiorista locale, coordinamento e consegna).</p>}
            </div>
            <button type="button" onClick={() => setEditing(true)} className="shrink-0 text-xs text-forest underline underline-offset-2 inline-flex items-center gap-1 hover:text-brand" data-testid="fallback-finder-change">
              <Pencil className="h-3 w-3" />Cambia
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold text-forest flex items-center gap-2"><MapPin className="h-4 w-4 text-brand" />Dove consegniamo?</p>
          <p className="text-xs text-sage mt-1 mb-3">Scrivi il comune (basta la città): se c'è un fiorista affiliato ti proponiamo lo stesso articolo preparato da lui, altrimenti ci pensa Melaverde.</p>
          <DeliverySearchBar variant="compact" onFound={onFound} />
          {saved && <button type="button" onClick={() => setEditing(false)} className="mt-2 text-xs text-sage underline underline-offset-2" data-testid="fallback-finder-cancel">Annulla</button>}
        </>
      )}
      {result?.type === "fallback" && (
        <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="fallback-finder-result">
          <LifeBuoy className="h-3.5 w-3.5 mt-0.5 shrink-0" />Nella tua zona non c'è ancora un fiorista affiliato: l'ordine è gestito da {result.shop_name}, servizio di copertura Flowerlive. Puoi procedere con l'acquisto.
        </p>
      )}
      <p className="mt-3 text-[11px] text-sage flex items-center gap-1"><Sparkles className="h-3 w-3 text-brand" />Stessa qualità, stesso prezzo consigliato <Check className="h-3 w-3 text-brand ml-1" /></p>
    </div>
  );
};
